import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export const ROLES = {
  management: {
    id: "management", label: "Management", short: "MD",
    desc: "Executive & business decision making",
    canAdmin: false,
  },
  plant_head: {
    id: "plant_head", label: "Plant Head", short: "PH",
    desc: "Operational management & performance analysis",
    canAdmin: false,
  },
  administrator: {
    id: "administrator", label: "Administrator", short: "AD",
    desc: "System ownership & platform administration",
    canAdmin: true,
  },
};

export function AppProvider({ children, initialRole = "administrator" }) {
  const [role, setRole] = useState(initialRole);
  const [snapshot, setSnapshot] = useState(null);
  const [sim, setSim] = useState({ status: "running", sim_mode_active: true, active_anomalies: [] });
  const [system, setSystem] = useState(null);
  const [connected, setConnected] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const snap = await api.stateCurrent();
      setSnapshot(snap);
      setSim({
        status: snap.sim_status,
        sim_mode_active: snap.sim_mode_active,
        active_anomalies: snap.active_anomalies || [],
      });
      setConnected(true);
    } catch (e) {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    api.systemInfo().then(setSystem).catch(() => {});
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  // Admin actions
  const act = {
    start: async () => { await api.simStart(); refresh(); },
    pause: async () => { await api.simPause(); refresh(); },
    resume: async () => { await api.simResume(); refresh(); },
    stop: async () => { await api.simStop(); refresh(); },
    inject: async (id) => { await api.injectAnomaly(id); refresh(); },
    clear: async () => { await api.clearAnomalies(); refresh(); },
    reset: async () => { await api.simReset(); refresh(); },
    factoryReset: async () => { await api.factoryReset(); refresh(); },
  };

  const value = {
    role, setRole, roleMeta: ROLES[role],
    snapshot, sim, system, connected, refresh, act,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
